import { useEffect, useRef, useState } from "react";

export function useZoomPan(enabled: boolean) {
  const containerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const panRef = useRef({ isPanning: false, startX: 0, startY: 0 });
  const pointers = useRef<Map<number, PointerEvent>>(new Map());
  const lastPinchDistance = useRef<number | null>(null);

  const clampTranslate = (x: number, y: number) => {
    const container = containerRef.current;
    const img = imgRef.current;
    if (!container || !img) return { x, y };

    const containerRect = container.getBoundingClientRect();
    const imgWidth = img.naturalWidth * scale;
    const imgHeight = img.naturalHeight * scale;

    const maxX = Math.max(0, (imgWidth - containerRect.width) / 2);
    const maxY = Math.max(0, (imgHeight - containerRect.height) / 2);

    return {
      x: Math.min(maxX, Math.max(-maxX, x)),
      y: Math.min(maxY, Math.max(-maxY, y)),
    };
  };

  useEffect(() => {
    if (!enabled || scale === 1) {
      setTranslate({ x: 0, y: 0 });
    }
  }, [enabled, scale]);

  const handleWheel = (e: React.WheelEvent) => {
    if (!enabled) return;
    e.preventDefault();
    const delta = -e.deltaY * 0.001;
    setScale((prev) => {
      const next = Math.min(Math.max(prev + delta * prev, 1), 5);
      return next;
    });
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (scale === 1) return;
    panRef.current.isPanning = true;
    panRef.current.startX = e.clientX - translate.x;
    panRef.current.startY = e.clientY - translate.y;
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!panRef.current.isPanning) return;
      const newX = e.clientX - panRef.current.startX;
      const newY = e.clientY - panRef.current.startY;
      setTranslate(clampTranslate(newX, newY));
    };

    const handleMouseUp = () => {
      panRef.current.isPanning = false;
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
 // eslint-disable-next-line
  }, [scale ]);

  // 👇 Pinch-zoom via pointer events (touch support, touchpad gesture support)
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !enabled) return;

    const onPointerDown = (e: PointerEvent) => {
      pointers.current.set(e.pointerId, e);
      container.setPointerCapture(e.pointerId);
    };

    const onPointerMove = (e: PointerEvent) => {
      if (!pointers.current.has(e.pointerId)) return;
      pointers.current.set(e.pointerId, e);

      if (pointers.current.size === 2) {
        const [p1, p2] = Array.from(pointers.current.values());

        const dx = p1.clientX - p2.clientX;
        const dy = p1.clientY - p2.clientY;
        const dist = Math.hypot(dx, dy);

        if (lastPinchDistance.current != null) {
          const delta = dist / lastPinchDistance.current;
          setScale((prev) => {
            const next = Math.min(Math.max(prev * delta, 1), 5);
            return next;
          });
        }

        lastPinchDistance.current = dist;
      }
    };

    const onPointerUp = (e: PointerEvent) => {
      pointers.current.delete(e.pointerId);
      if (pointers.current.size < 2) {
        lastPinchDistance.current = null;
      }
    };

    container.addEventListener("pointerdown", onPointerDown);
    container.addEventListener("pointermove", onPointerMove);
    container.addEventListener("pointerup", onPointerUp);
    container.addEventListener("pointercancel", onPointerUp);

    return () => {
      container.removeEventListener("pointerdown", onPointerDown);
      container.removeEventListener("pointermove", onPointerMove);
      container.removeEventListener("pointerup", onPointerUp);
      container.removeEventListener("pointercancel", onPointerUp);
    };
  }, [enabled]);

  const reset = () => {
    setScale(1);
    setTranslate({ x: 0, y: 0 });
  };

  return {
    containerRef,
    imgRef,
    scale,
    translate,
    handleWheel,
    handleMouseDown,
    reset,
  };
}
